// Every calendar edit is a v2 command (§13.14; aithlo-backend#195): moves,
// edits (title, time, duration, description, RPE, what was done, the planned
// and actual numbers, the steps from the structured editor), adding a session of any sport,
// rest days, a session turned into a race and back, races, notes, and linking
// an activity (POST /v2/activities/{activity_uuid}/link). Each is one change
// with Undo in the thread. What v2 can't take is refused here with words the
// athlete can act on, never sent to a v1 /plan write.

import { commands } from './commands';

// The calendar's sports → the v2 Sport vocabulary (contracts/v2). Rest and
// note rows aren't sessions in v2. Any other word (yoga, walk…) is sent as
// it is: the backend keeps it and counts it as the nearest v2 sport.
export const V2_SPORT = { run: 'run', bike: 'bike', swim: 'swim', gym: 'strength' };
const NOT_SESSIONS = new Set(['rest', 'note', 'race']);
const sportWord = (s) => String(s || '').trim().toLowerCase();
/** The word POST /v2/sessions and PATCH take for a calendar sport; null for rest, note or race. */
export const v2SportOf = (sport) => {
  const w = sportWord(sport);
  if (!w || NOT_SESSIONS.has(w)) return null;
  return V2_SPORT[w] || w;
};

// The planned and actual numbers PATCH /v2/sessions takes as they are.
const METRICS = ['distance_m', 'duration_sec', 'avg_hr', 'avg_power', 'avg_pace_sec', 'avg_cadence', 'elevation_gain_m', 'tss'];
const PASS = new Set(['title', 'time_of_day', 'description', 'rpe', 'planned_rpe', 'actual_notes', 'planned_gpx',
  ...METRICS.map((m) => `planned_${m}`), ...METRICS.map((m) => `actual_${m}`)]);
// What a session's own row carries that is not a planned target: logged after the fact.
const LOGGED = new Set(['rpe', 'actual_notes', ...METRICS.map((m) => `actual_${m}`)]);
// Race keys: a session save no longer carries them (races have their own sheet); skipped if one does.
const RACE_KEYS = new Set(['is_race', 'race_priority', 'race_details']);
const RACE_EDITABLE = new Set(['title', 'new_date', 'sport', 'race_priority', 'race_details', 'description', 'planned_gpx']);
const LETTER = { a_race: 'A', b_race: 'B', c_race: 'C' };

/** What the athlete reads when a coach session's sport is changed here (its steps come with its sport). */
export const STEPS_TEXT = "This session's sport comes with its steps: to make it another sport, ask your coach in the thread, or add a new session.";
/** A coach session's steps saved with no intervals left. */
export const EMPTY_STEPS_TEXT = 'A session needs at least one interval: add one back, or remove the session.';
export const NEW_STEPS_TEXT = 'Steps from the editor can\'t be saved on a new session yet. Clear them to save it, then ask your coach to write the steps.';
/** While the v2 client is still starting: nothing is sent anywhere else. */
export const CONNECTING_TEXT = 'Still connecting to your coach: try again in a moment.';

/** The session has v2 steps (training_plan.workout.segment): the editor's blocks are sent as `structure`. */
export const hasV2Steps = (s) => Boolean(s?.workout && typeof s.workout === 'object' && s.workout.segment);

// training_plan.origin values that are the athlete's own sessions (v2_rows.ATHLETE_ORIGINS).
const ATHLETE_ORIGINS = new Set(['user', 'club', 'adopted_activity']);
/** A session the coach wrote. */
export const isCoachSession = (s) => isStoredSession(s) && !ATHLETE_ORIGINS.has(String(s?.origin || 'coach'));
/**
 * A coach session the athlete has made theirs by editing it (training_plan.locked):
 * the coach plans around it and only proposes changes, until they hand it back
 * (POST /v2/sessions/{id}/release). Not once it's done.
 */
export const isTakenOver = (s) => isV2Session(s) && isCoachSession(s) && Boolean(s.locked) && !s.completed
  && !['completed', 'missed', 'skipped'].includes(s.state);

/** A race's GPX as the details v2 races keep (they carry no GPX column of their own). */
const gpxDetails = (gpx) => (gpx ? {
  gpx_distance_m: gpx.distance_m ?? null, gpx_elevation_gain_m: gpx.elevation_gain_m ?? null,
  gpx_polyline: gpx.polyline ?? null, gpx_filename: gpx.source_filename ?? null,
} : { gpx_distance_m: null, gpx_elevation_gain_m: null, gpx_polyline: null, gpx_filename: null });

/** The race's details with the modal's edits laid on (PATCH /v2/races replaces `details` whole). */
function raceDetails(session, patch) {
  const touched = patch.race_details || patch.description !== undefined || patch.planned_gpx !== undefined;
  if (!touched) return undefined;
  return {
    ...(session.race_details || {}), ...(patch.race_details || {}),
    ...(patch.description !== undefined ? { notes: patch.description || '' } : {}),
    ...(patch.planned_gpx !== undefined ? gpxDetails(patch.planned_gpx) : {}),
  };
}

/** A race's edit routed here: PATCH /v2/races. Session ↔ race is commands.toRace / toSession, from each sheet's ⋯ menu. */
function raceEdit(session, patch) {
  const keys = Object.keys(patch || {});
  if (!keys.length) return null;
  const bad = keys.filter((k) => !RACE_EDITABLE.has(k));
  if (bad.length) return { error: "That can't be changed on a race here. Turn it back into a session first." };
  const details = raceDetails(session, patch);
  const sport = patch.sport && sportWord(patch.sport) !== 'race' ? sportWord(patch.sport) : null;
  const fields = {
    ...(patch.title != null && patch.title.trim() ? { title: patch.title.trim() } : {}),
    ...(patch.new_date ? { date: patch.new_date } : {}),
    ...(sport ? { sport } : {}),
    ...(patch.race_priority && LETTER[patch.race_priority] ? { priority: LETTER[patch.race_priority] } : {}),
    ...(details ? { details } : {}),
  };
  return Object.keys(fields).length ? { commands: [commands.editRace(session, fields)] } : null;
}

/** A real training_plan row (not one the queue is still creating). */
export const isStoredSession = (s) => Number.isInteger(s?.id);

/** A row the v2 session commands act on: a stored training session of any sport (not a race, rest day or note). */
export const isV2Session = (s) => isStoredSession(s) && !s.is_race && Boolean(v2SportOf(s.sport));

/** A stored rest row (GET /plan's sport 'rest'): POST/DELETE /v2/days/{date}/rest. */
export const isRestRow = (s) => isStoredSession(s) && sportWord(s.sport) === 'rest';

/** Tick or un-tick a v2 session (the row as the calendar shows it); null: not a session. */
export const v2DoneCommand = (session) => (isV2Session(session) ? commands.done(session, !session.completed) : null);

/** Two v2 sessions trading days, as one command; null: not two sessions. */
export const v2SwapCommand = (a, b) => (isV2Session(a) && isV2Session(b) ? commands.swap(a, b) : null);

/** The sessions on a day a rest day would take off (planned, not done, not a race). */
export const plannedOn = (rows, date) => (rows || []).filter((r) => r.date === date && isV2Session(r) && !r.completed
  && r.state !== 'completed');

/** A rest day moved from `from` to `to` (no clash there): the day stops being one, the other starts. */
export const restMoveCommands = (from, to) => [commands.clearRest(from), commands.restDay(to)];

/**
 * A rest day dropped on a busy day (Calendar's RestMoveModal): what happens
 * to each session that was there. 'swap' moves them to the rest day's old
 * date, 'reassign' to the day picked for each, 'remove' takes them off in the
 * rest day's own change. The rest day leaves its old day first, so the
 * sessions can land there.
 */
export function restCollisionWrites({ mode, restDate, targetDate, targetSessions, reassignments = {} }) {
  const out = [commands.clearRest(restDate)];
  if (mode === 'remove') return [...out, commands.restDay(targetDate, { clear: true })];
  for (const s of targetSessions || []) {
    const to = mode === 'swap' ? restDate : reassignments[s.id];
    if (to && to !== s.date && isV2Session(s)) out.push(commands.move(s, to));
  }
  return [...out, commands.restDay(targetDate)];
}

/**
 * What a calendar row is, for the controls it offers (aithlo-frontend#62):
 * 'outline' (a planned session of an outline week), 'draft' (a proposed
 * session), 'rest' (a written week's empty day, overlay.restRows), 'pending' (one
 * the queue is still creating), 'note' (a v2 note),
 * 'row' (a real training_plan row with an id) or 'unknown'.
 */
export function rowKind(s) {
  if (s?._outline) return 'outline';
  if (s?._rest) return 'rest';
  if (s?._draft) return 'draft';
  if (String(s?.id ?? '').startsWith('pending:') || String(s?._noteId ?? '').startsWith('pending:')) return 'pending';
  if (s?._noteId != null) return 'note';
  if (isStoredSession(s)) return 'row';
  return 'unknown';
}

/**
 * The calendar card's controls for a row. Only a real row is deleted, ticked
 * off, linked to an activity or opened in the editable session detail, and
 * always by its id (the v2 commands' session id): an outline, draft or
 * still-saving row isn't a row there, and its per-day index would hit another.
 * `open`: 'detail' (SessionDetailModal), 'outline' (OutlineSessionSheet),
 * 'thread' (a draft is answered on its proposal) or null.
 */
export function rowControls(s) {
  const kind = rowKind(s);
  const row = kind === 'row';
  const open = { outline: 'outline', draft: 'thread', row: 'detail', note: 'detail' }[kind] || null;
  return { kind, open, canDelete: row || kind === 'note', canMarkDone: row, canLink: row };
}

/** The session modal's patch → PATCH /v2/sessions fields; `{ error }` when a key has no v2 form. */
function sessionFields(session, patch) {
  const fields = {};
  for (const [k, val] of Object.entries(patch)) {
    if (k === 'title') { if (val != null && val.trim()) fields.title = val.trim(); continue; }
    if (k === 'planned_duration_sec') {
      fields.planned_duration_sec = val > 0 ? Math.round(val) : null;
      if (val > 0) fields.estimated_duration_s = Math.round(val);
      continue;
    }
    if (k === 'time_of_day') { fields.time_of_day = val || null; continue; }
    if (PASS.has(k)) { fields[k] = val; continue; }
    if (k === 'workout') {
      // A v1 session's blocks are kept as sent. A v2 session's blocks are sent as
      // `structure`: the backend stores them as its steps, exactly as edited.
      if (hasV2Steps(session)) {
        const blocks = Array.isArray(val?.structure) ? val.structure : [];
        if (!blocks.some((b) => Array.isArray(b?.intervals) && b.intervals.length)) return { error: EMPTY_STEPS_TEXT };
        fields.structure = blocks;
        continue;
      }
      fields.workout = val;
      continue;
    }
    if (k === 'sport') {
      if (hasV2Steps(session)) return { error: STEPS_TEXT };
      const w = v2SportOf(val);
      if (!w) return { error: "A session's sport can't be rest, a note or a race here." };
      fields.sport = w;
      continue;
    }
    if (k === 'new_date' || k === 'completed' || RACE_KEYS.has(k)) continue;
    return { error: "That change can't be saved here." };
  }
  return { fields };
}

/**
 * The commands for a SessionDetailModal save, or `{ error }` when the athlete
 * must change something first. Null: nothing to send.
 */
export function v2SaveCommands({ isDraft, session, patch, payload }) {
  if (session?._note) {
    // A v2 note (calendar_notes): only its text is edited here.
    const keys = Object.keys(patch || {});
    if (!keys.length) return null;
    if (keys.some((k) => k !== 'description' && k !== 'title')) return { error: "Only a note's text can be changed here." };
    if (patch.description == null || !patch.description.trim()) return { error: 'A note needs some text.' };
    const n = session._note;
    return { commands: [commands.editNote(n, { start_date: n.start_date, end_date: n.end_date, text: patch.description.trim() })] };
  }
  if (isDraft) return newSession(payload || {});
  if (session?.is_race && isStoredSession(session)) return raceEdit(session, patch);
  const keys = Object.keys(patch || {});
  if (!keys.length) return null;
  if (isRestRow(session)) {
    if (keys.some((k) => k !== 'new_date')) return { error: 'A rest day has nothing else to change: move it, or remove it.' };
    return { commands: restMoveCommands(session.date, patch.new_date) };
  }
  if (!isV2Session(session)) return { error: "That change can't be saved here." };
  const { fields, error } = sessionFields(session, patch);
  if (error) return { error };
  const out = [];
  if (Object.keys(fields).length) out.push(commands.edit(session, fields));
  if (patch.completed === true && !session.completed) out.push(commands.done(session, true));
  if (patch.new_date) out.push(commands.move(session, patch.new_date));
  return out.length ? { commands: out } : null;
}

/** A session added from the session modal: POST /v2/sessions, then what was already done on it. */
function newSession(payload) {
  const sport = v2SportOf(payload.sport);
  if (!sport) return { error: "Add a rest day or a note from the day's menu." };
  const structure = payload.workout?.structure;
  if (Array.isArray(structure) && structure.length) return { error: NEW_STEPS_TEXT };
  const minutes = Math.round((payload.planned_duration_sec || 0) / 60);
  if (minutes < 5) return { error: 'Add a planned duration (at least 5 minutes) to save this session.' };
  if (sport === 'swim' && !payload.planned_distance_m) return { error: "Add the swim's distance in metres." };
  // What was done (the "How it went" RPE, actuals, notes) and the other planned
  // numbers go on the new session once it exists; the RPE is never its planned effort.
  const fields = {};
  for (const [k, val] of Object.entries(payload)) {
    if (val == null || val === '') continue;
    const sentOnCreate = ['planned_duration_sec', 'planned_rpe', ...(sport === 'swim' ? ['planned_distance_m'] : [])];
    if (LOGGED.has(k) || (PASS.has(k) && k.startsWith('planned_') && !sentOnCreate.includes(k))) fields[k] = val;
  }
  const then = [];
  if (Object.keys(fields).length) then.push({ fields });
  if (payload.completed) then.push({ done: true });
  return { commands: [commands.create({
    date: payload.date, sport, title: (payload.title || '').trim() || 'Session', minutes,
    effort_rpe: payload.planned_rpe || 4,
    time_of_day: payload.time_of_day || null,
    distance_m: sport === 'swim' ? Math.round(payload.planned_distance_m) : null,
    description: payload.description || null,
    then,
  })] };
}

/** The words for an answer's `sport_mapped` ({from, to}): the app says how the coach counts the session. */
export function sportMappedText(mapped) {
  if (!mapped?.from || !mapped?.to || mapped.from === mapped.to) return null;
  const to = { mobility: 'mobility work', other: 'other training', strength: 'strength' }[mapped.to] || mapped.to;
  return `Saved as ${mapped.from}. Your coach counts it as ${to}.`;
}

/** Days → the fewest date ranges (a note over consecutive days is one v2 note). */
export function dayRanges(days) {
  const sorted = [...new Set(days)].sort();
  const out = [];
  for (const d of sorted) {
    const last = out[out.length - 1];
    const next = last && new Date(`${last.end}T00:00:00Z`);
    if (next) next.setUTCDate(next.getUTCDate() + 1);
    if (next && next.toISOString().slice(0, 10) === d) last.end = d;
    else out.push({ start: d, end: d });
  }
  return out;
}

/** The v2 store's activity for a Strava id (its `id` is what /v2/activities/{id}/link takes). */
export function v2Activity(cal, stravaActivityId) {
  if (!cal?.activities || stravaActivityId == null) return null;
  const sid = String(stravaActivityId);
  return Object.values(cal.activities).find((a) => String(a?.strava_activity_id) === sid && a?.id) || null;
}

/** When an activity can't be linked yet (its v2 id isn't known: it hasn't reached your coach). */
export const LINK_UNKNOWN_TEXT = "That activity hasn't reached your coach yet. Give it a minute, then try again.";

const LINKABLE = (s) => isStoredSession(s) && !['rest', 'note'].includes(String(s.sport || '').toLowerCase());

/**
 * Link an activity to a session (connect, adopt into a session), or say it
 * was extra (`session` null: disconnect, adopt standalone). `activity`: the
 * calendar's activity row (/activities/user: `strava_activity_id`, and
 * `activity_uuid` since aithlo-backend#181), or just its Strava id. Null when
 * its uuid isn't known (not in the row, not in the v2 store's window) or the
 * row isn't a stored session or race: the calendar says so (LINK_UNKNOWN_TEXT).
 */
export function linkCommand(cal, activity, session) {
  const row = activity && typeof activity === 'object' ? activity : { strava_activity_id: activity };
  const stored = row.activity_uuid ? null : v2Activity(cal, row.strava_activity_id);
  const uuid = row.activity_uuid || stored?.id;
  if (!uuid) return null;
  if (session && !LINKABLE(session)) return null;
  return commands.link({ id: String(uuid), strava_activity_id: row.strava_activity_id ?? stored?.strava_activity_id ?? null,
    name: row.name || stored?.name || null }, session || null);
}

const DECIDED = new Set(['athlete', 'confirmed']);

/**
 * Where the athlete has put each activity (Strava id → session id, or null
 * for "it was extra"): the v2 store's athlete-decided links (matched_by
 * 'athlete' or 'confirmed'), with link commands still on their way on top.
 * The calendar's same-day auto-match never overrides these.
 */
export function athleteLinks(cal, pending = []) {
  const out = new Map();
  for (const a of Object.values(cal?.activities || {})) {
    if (a?.strava_activity_id != null && DECIDED.has(a.matched_by)) out.set(String(a.strava_activity_id), a.session_id ?? null);
  }
  for (const c of pending || []) {
    if (c.op?.kind === 'link' && c.op.strava_activity_id != null) out.set(String(c.op.strava_activity_id), c.op.session_id ?? null);
  }
  return out;
}

// ---- a plan built from the calendar (aithlo-frontend#67; mobile's aithlo-mobile#60) ----

/** What the calendar's Regenerate Plan names its rebuild by: there is no thread item behind it. */
export const PLAN_BUILD_ID = 'calendar';

/** Regenerate Plan: POST /v2/plan/rebuild (the coach writes a fresh strategy; it arrives in the thread). */
export const planBuildCommand = () => commands.rebuildPlan({ id: PLAN_BUILD_ID });

/**
 * The calendar's rebuild is asked for: on its way, or answered and not yet
 * done (the client drops the answer once the draft it started ends: client.js).
 */
export const planBuildAsked = (pending = [], outcomes = {}, now = Date.now()) => {
  const o = outcomes?.[PLAN_BUILD_ID];
  // A build expected without its command (onboarding: client.expectBuild)
  // carries when it started: past BUILD_WAIT_MS it isn't "on it" any more.
  const fresh = o?.status === 'rebuilding' && !(Number.isFinite(o.since) && now - o.since > BUILD_WAIT_MS);
  return fresh || (pending || []).some((c) => c.op?.kind === 'rebuild' && String(c.op.id) === PLAN_BUILD_ID);
};

/** How long an expected build (client.expectBuild) shows the coach on it with nothing from its run. */
export const BUILD_WAIT_MS = 10 * 60 * 1000;
